<style lang="scss" scoped>
.page-wrap {
  padding: 8px 0 80px;
  font-family: 'Segoe UI', sans-serif;
}

.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #eef2fb;
  border-bottom: 1px solid #dde4f5;

  .panel-title {
    font-size: 13px;
    font-weight: 700;
    color: #389ad4;
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f3fa;

  .search-wrap {
    position: relative;
    flex: 1;
    max-width: 320px;

    .search-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #a0aab8;
      font-size: 13px;
    }

    input {
      width: 100%;
      height: 38px;
      border: 1.5px solid #dde2ec;
      border-radius: 8px;
      padding: 0 12px 0 34px;
      font-size: 13px;
      color: #0d1b3e;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;

      &:focus {
        border-color: #389ad4;
        box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
      }

      &::placeholder {
        color: #b0bac9;
      }
    }
  }

  .btn-add {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 18px;
    background: #F58020;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;
    white-space: nowrap;
    margin-left: auto;

    &:hover {
      opacity: 0.9;
    }
  }
}

.sc-table {
  width: 100%;
  font-size: 13px;
  border-collapse: collapse;

  thead tr th {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #7a8499;
    padding: 10px 16px;
    border-bottom: 2px solid #eef0f8;
    background: #fff;
    white-space: nowrap;
  }

  tbody tr {
    border-bottom: 1px solid #f5f7fa;
    transition: background 0.15s;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #f8fbff;
    }

    td {
      padding: 11px 16px;
      color: #2d3748;
      vertical-align: middle;
    }
  }

  .col-sn {
    width: 48px;
    color: #a0aab8;
    font-size: 12px;
  }

  .col-name {
    font-weight: 400;
    color: #0d1b3e;
  }

  .col-code {
    font-family: monospace;
    font-size: 12px;
    background: #eef2fb;
    color: #185FA5;
    padding: 3px 8px;
    border-radius: 4px;
    display: inline-block;
  }

  .col-num {
    color: #4a5568;
  }

  .btn-edit {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 14px;
    background: #eef2fb;
    color: #185FA5;
    border: 1px solid #c5d4f0;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #389ad4;
      color: #fff;
      border-color: #389ad4;
    }
  }
}

.empty-state {
  text-align: center;
  padding: 48px 24px;
  color: #a0aab8;
  font-size: 13px;
}

.loading-state {
  text-align: center;
  padding: 40px;
}
</style>

<style>
.swal2-popup .swal2-icon {
  margin-left: 39% !important;
}
</style>

<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <span class="panel-title">
          <i class="fa fa-list-alt"></i>
          Service Codes
        </span>
        <span style="font-size:12px; color:#7a8499;">
          {{ filteredServiceCode.length }} record{{ filteredServiceCode.length !== 1 ? 's' : '' }}
        </span>
      </div>

      <!-- Toolbar -->
      <div class="toolbar">
        <div class="search-wrap">
          <i class="fa fa-search search-icon"></i>
          <input v-model="searchInput" type="text" placeholder="Search by name or code…" />
        </div>
        <button class="btn-add" @click="showModal">
          <i class="fa fa-plus"></i> Add Service Code
        </button>
      </div>

      <!-- Table -->
      <table class="sc-table">
        <thead>
          <tr>
            <th class="col-sn">#</th>
            <th>Name</th>
            <th>Code</th>
            <th>Delete In (Days)</th>
            <th>Default Cost</th>
            <th>Expedited Cost</th>
            <th>Expedited Markup</th>
            <th>Default Markup</th>
            <th>Default Tax</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody v-if="loadingData">
          <tr>
            <td colspan="10" class="loading-state">
              <vDataLoader></vDataLoader>
            </td>
          </tr>
        </tbody>

        <tbody v-else-if="filteredServiceCode.length === 0">
          <tr>
            <td colspan="10" class="empty-state">
              <i class="fa fa-inbox" style="font-size:28px; display:block; margin-bottom:8px;"></i>
              No service codes found
            </td>
          </tr>
        </tbody>

        <tbody v-else>
          <tr v-for="(item, index) in filteredServiceCode" :key="index">
            <td class="col-sn">{{ index + 1 }}</td>
            <td class="col-name">{{ item.service_name }}</td>
            <td><span class="col-code">{{ item.code }}</span></td>
            <td class="col-num">{{ item.days }}</td>
            <td class="col-num">{{ item.default_cost }}</td>
            <td class="col-num">{{ item.default_expedited_cost }}</td>
            <td class="col-num">{{ item.default_expedited_markup }}</td>
            <td class="col-num">{{ item.default_markup }}</td>
            <td class="col-num">{{ item.default_tax }}</td>
            <td>
              <button class="btn-edit" @click="edit(item)">
                <i class="fa fa-pencil"></i> Edit
              </button>
            </td>
          </tr>
        </tbody>
      </table>

    </div>

    <servicecode v-if="ModalShow" v-on:closemodal="closeModal" :show-modal="ModalShow" :servicecode="serviceCodeData"
      :is_edit="is_edit"></servicecode>
  </div>
</template>

<script>
import { FETCH_SERVICECODES } from "@/core/services/store/actions.type";
import servicecode from "@/pages/backend/servicecodes/servicecode";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import vDataLoader from "@/components/frontend/spinner.vue";

export default {
  computed: {
    ...mapGetters(["currentUser", "getServiceCodeData"]),
    filteredServiceCode() {
      let tempServiceCode = this.getServiceCodeData;
      if (this.searchInput !== "" && this.searchInput) {
        tempServiceCode = tempServiceCode.data.filter(item => {
          return (
            item.service_name.toUpperCase().includes(this.searchInput.toUpperCase()) ||
            item.code.toUpperCase().includes(this.searchInput.toUpperCase())
          );
        });
      } else {
        tempServiceCode = tempServiceCode.data;
      }
      return tempServiceCode;
    }
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.getServiceCode();
  },
  components: { servicecode, vDataLoader },
  methods: {
    showModal() {
      this.serviceCodeData = {
        id: 0,
        service_name: "",
        code: "",
        days: "",
        default_markup: "",
        default_tax: ""
      };
      this.ModalShow = true;
      this.is_edit = false;
    },
    closeModal() {
      this.ModalShow = false;
      this.$store.dispatch(FETCH_SERVICECODES);
    },
    edit(item) {
      this.ModalShow = true;
      this.is_edit = true;
      this.serviceCodeData = { ...item };
    },
    getServiceCode() {
      this.loadingData = true;
      this.$store
        .dispatch(FETCH_SERVICECODES)
        .then(() => { this.loadingData = false; })
        .catch(() => { this.loadingData = false; });
    }
  },
  data() {
    return {
      loadingData: false,
      ModalShow: false,
      searchInput: "",
      is_edit: false,
      serviceCodeData: {
        id: 0,
        service_name: "",
        code: "",
        days: "",
        default_markup: "",
        default_tax: ""
      },
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Service Code List", route: "" }
      ]
    };
  }
};
</script>
